import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { useAuth } from '../../hooks';
import { apiClient } from '../../api/client';
import Text from '../Text/Text';
import './BankAccountForm.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

interface BankAccountFormProps {
  onSuccess?: () => void;
  onError?: (error: any) => void;
  onCancel?: () => void;
  isUpdate?: boolean;
  defaultValues?: {
    accountHolderName?: string;
    accountHolderType?: 'individual' | 'company';
  };
}

const BankAccountForm: React.FC<BankAccountFormProps> = ({
  onSuccess,
  onError,
  onCancel,
  isUpdate = false,
  defaultValues
}) => {
  const [stripe, setStripe] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { currentUser } = useAuth();

  useEffect(() => {
    const initializeStripe = async () => {
      const stripeInstance = await stripePromise;
      setStripe(stripeInstance);
    };

    initializeStripe();
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    
    if (!stripe) {
      setError('Stripe has not been initialized yet. Please try again.');
      return;
    }

    if (!currentUser?.stripeDetails?.stripeConnectedAccountId) {
      setError('You do not have a connected account. Please try again later.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const form = event.target as HTMLFormElement;
      const accountHolderName = (form.elements.namedItem('accountHolderName') as HTMLInputElement).value;
      const routingNumber = (form.elements.namedItem('routingNumber') as HTMLInputElement).value;
      const accountNumber = (form.elements.namedItem('accountNumber') as HTMLInputElement).value;
      const accountHolderType = (form.elements.namedItem('accountHolderType') as HTMLSelectElement).value;

      // Create a bank account token
      const { token, error: tokenError } = await stripe.createToken('bank_account', {
        country: 'AU',
        currency: 'aud',
        routing_number: routingNumber,
        account_number: accountNumber,
        account_holder_name: accountHolderName,
        account_holder_type: accountHolderType,
      });

      if (tokenError) {
        setError(tokenError.message);
        setLoading(false);
        if (onError) onError(tokenError);
        return;
      }

      // Send the token to the server
      const response = await apiClient.stripe.addExternalAccount(token.id);

      if (response.success) {
        setSuccess(true);
        setLoading(false);
        if (onSuccess) onSuccess();
      } else {
        setError('Failed to add bank account. Please try again.');
        setLoading(false);
        if (onError) onError(new Error('Failed to add bank account'));
      }
    } catch (err) {
      console.error('Error adding bank account:', err);
      setError('An error occurred while adding your bank account. Please try again.');
      setLoading(false);
      if (onError) onError(err);
    }
  };

  if (success) {
    return (
      <div className="bank-account-success">
        <Text className="text-body">Bank account {isUpdate ? 'updated' : 'added'} successfully!</Text>
        <Text className="text-caption">Your bank account has been {isUpdate ? 'updated in' : 'linked to'} your VeriPay account.</Text>
      </div>
    );
  }

  return (
    <div className="bank-account-form-container">
        <div>
            <Text className="text-body">{isUpdate ? 'Update' : 'Add'} Bank Account</Text>
        </div>
      <Text className="text-caption">Enter your bank account details below to receive payments.</Text>
      
      {error && (
        <div className="bank-account-error">
          <Text className="text-caption">{error}</Text>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="bank-account-form">
        <div className="bank-account-form-group">
          <label htmlFor="accountHolderName">Account Holder Name</label>
          <input
            type="text"
            id="accountHolderName"
            name="accountHolderName"
            placeholder="John Doe"
            defaultValue={defaultValues?.accountHolderName || ''}
            required
          />
        </div>
        
        <div className="bank-account-form-group">
          <label htmlFor="accountHolderType">Account Type</label>
          <select
            id="accountHolderType"
            name="accountHolderType"
            defaultValue={defaultValues?.accountHolderType || 'individual'}
            required
          >
            <option value="individual">Individual</option>
            <option value="company">Company</option>
          </select>
        </div>
        
        <div className="bank-account-form-group">
          <label htmlFor="routingNumber">BSB Number</label>
          <input
            type="text"
            id="routingNumber"
            name="routingNumber"
            placeholder="123456"
            required
            pattern="[0-9]{6}"
            title="BSB Number must be 6 digits"
          />
        </div>
        
        <div className="bank-account-form-group">
          <label htmlFor="accountNumber">Account Number</label>
          <input
            type="text"
            id="accountNumber"
            name="accountNumber"
            placeholder="12345678"
            required
            pattern="[0-9]{6,10}"
            title="Account Number must be 6-10 digits"
          />
        </div>
        
        <div className="bank-account-form-buttons">
          <button
            type="submit"
            className="submit-button"
            disabled={loading || !stripe}
          >
            {loading ? `${isUpdate ? 'Updating' : 'Adding'} Bank Account...` : `${isUpdate ? 'Update' : 'Add'}`}
          </button>
          
          {isUpdate && onCancel && (
            <button
              type="button"
              className="cancel-button"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default BankAccountForm;